import React, { useCallback } from 'react';
import type { ReactNode } from 'react';
import memoize from 'memoize-one';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { refetchEditorContent } from '@confluence/editor-features/entry-points/refetchEditorContent.ts';
import { EditorContextPanelContentCreationPanel } from '@confluence/editor-context-panel-content-creation-panel';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import type { PanelMode } from '@confluence/template-utils/entry-points/urlUtils.ts';

import { useEditorContextPanel } from './useEditorContextPanel.ts';

const renderContextPanelContentCreationPanel = memoize(
	({
		// TODO: canPublish is used for isPanelDisabled, but and it includes a permission check in
		// addition to collab init check which needs to be added for NativeCollab
		// canPublish
		cancelDebouncedSaveDraft,
		contentType,
		collabEditInitialized,
		beforeUpdateRequest,
		recoverAndRefreshEditorContent,
		hideSpinner,
		hasUserAddedContent,
		isUnpublishedDraft,
		isContentCreationPanelOpen,
		onClickCloseContentCreationPanel,
		renderPanel,
		title,
		templateAllowList,
		isImportDisabled,
		isAlwaysDiscardChanges,
		isSpacePickerDisabled,
		isLivePage,
	}) => {
		// Render the templates content-creation panel for pages by default.
		// For blogposts, only render it when synced block patch 13 enables block templates support.
		const isSupportedContentType =
			contentType === 'page' ||
			(contentType === 'blogpost' && fg('platform_synced_block_patch_13'));
		const _renderPanel =
			renderPanel &&
			isSupportedContentType &&
			(isUnpublishedDraft ||
				isLivePage ||
				expVal('platform_editor_blocks_conf', 'isEnabled', false));
		if (_renderPanel) {
			return (
				<EditorContextPanelContentCreationPanel
					hasUserAddedContent={hasUserAddedContent}
					isPanelDisabled={!collabEditInitialized} // TODO: Add permission check here too
					title={title}
					onContentUpdated={recoverAndRefreshEditorContent}
					beforeUpdateRequest={beforeUpdateRequest}
					beforeTemplateSelect={cancelDebouncedSaveDraft}
					isContentCreationPanelShown={isContentCreationPanelOpen}
					onClickCloseContentCreationPanel={onClickCloseContentCreationPanel}
					onError={hideSpinner}
					templateAllowList={templateAllowList}
					isImportDisabled={isImportDisabled}
					isAlwaysDiscardChanges={isAlwaysDiscardChanges}
					isSpacePickerDisabled={isSpacePickerDisabled}
				/>
			);
		}

		return null;
	},
);

type Props = Parameters<typeof useEditorContextPanel>[0] & {
	children: (props: {
		isContentCreationPanelOpen: boolean;
		contentCreationPanelMode?: PanelMode;
		onClickOpenContentCreationPanel: (panelMode?: PanelMode) => void;
		beforeUpdateRequest: () => void;
		postUpdateRequest: () => void;
	}) => ReactNode;
	collabEditInitialized: boolean;
	contentType: string;
	renderPanel: boolean;
	title: string | null;
	templateAllowList?: string[];
	isSpacePickerDisabled?: boolean;
	isLivePage?: boolean;
};

export const EditorContextContentCreationPanelWrapper: React.FC<Props> = ({
	children,
	cancelDebouncedSaveDraft,
	collabEditInitialized,
	contentId,
	contentType,
	draftShareId,
	editorActions,
	hideSpinner,
	isUnpublishedDraft,
	renderPanel,
	showSpinner,
	title,
	isEmbeddedEditor,
	templateAllowList,
	isSpacePickerDisabled,
}) => {
	const {
		beforeUpdateRequest,
		onClickOpenContentCreationPanel,
		onClickCloseContentCreationPanel,
		postUpdateRequest,
		hasUserAddedContent,
		isContentCreationPanelOpen,
		contentCreationPanelMode,
		onUpdatedWithTemplateSubscriptionEvent,
	} = useEditorContextPanel({
		cancelDebouncedSaveDraft,
		contentId,
		draftShareId,
		editorActions,
		hideSpinner,
		showSpinner,
		isUnpublishedDraft,
		isEmbeddedEditor,
	});
	const isLivePage = useIsCurrentPageLive();

	const pubSubResourceType = contentType || 'page';

	useSubscriptionEvent({
		subscriberId: 'EditorContextPanelPageUpdatedWithTemplateSubscription',
		contentId,
		delta: 'contentUpdatedWithTemplate',
		contentType: pubSubResourceType,
		onDataReceived: onUpdatedWithTemplateSubscriptionEvent,
	});

	const recoverAndRefreshEditorContent = useCallback(async () => {
		await refetchEditorContent(contentId);
		postUpdateRequest();
	}, [contentId, postUpdateRequest]);

	return (
		<>
			{children({
				isContentCreationPanelOpen,
				contentCreationPanelMode,
				onClickOpenContentCreationPanel,
				beforeUpdateRequest,
				postUpdateRequest,
			})}
			{renderContextPanelContentCreationPanel({
				// canPublish,
				cancelDebouncedSaveDraft,
				collabEditInitialized,
				contentType,
				beforeUpdateRequest,
				recoverAndRefreshEditorContent,
				hasUserAddedContent,
				isUnpublishedDraft,
				isContentCreationPanelOpen,
				onClickCloseContentCreationPanel,
				hideSpinner,
				renderPanel,
				title,
				templateAllowList,
				isImportDisabled: isEmbeddedEditor,
				isAlwaysDiscardChanges: isEmbeddedEditor,
				isSpacePickerDisabled,
				isLivePage,
			})}
		</>
	);
};
